import { ErrorHandler, Injectable, OnDestroy } from '@angular/core'

import { AsiakkaanTuote, LaskunAsiakas, LaskuToimittaja, TypeaheadAsiakas, TypeaheadToimittaja, TypeaheadToimittajaDokumentti } from '@lemon/core-firebase/model/lasku'
import { FirestoreIndeksoija } from '@lemon/core-firebase/service/firestore.indeksoija'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonator.service'
import { BehaviorSubject, firstValueFrom, map, of, Subject, switchMap, takeUntil } from 'rxjs'
import {
  AsiakasTypeaheadResult,
  LaskunAsiakasTypeaheadBase,
  LaskunToimittajaTypeaheadBase,
  LaskunTuoteTypeaheadBase,
  ToimittajaTypeaheadResult,
  TuoteTypeaheadResult,
} from '../../../_jaettu-angular/service/lasku/typeahead.service'
import { AsiakasService } from '../../service/asiakas/asiakas.service'

@Injectable()
export class LaskunAsiakasTypeahead implements LaskunAsiakasTypeaheadBase {
  constructor() {}

  public annaAsiakas(typeaheadAsiakas: TypeaheadAsiakas): Promise<LaskunAsiakas> {
    return Promise.resolve(null)
  }

  public haeJaSuodata(value: string): Promise<AsiakasTypeaheadResult> {
    const res: AsiakasTypeaheadResult = {
      suodatetut: [],
    }
    return Promise.resolve(res)
  }
}

@Injectable()
export class LaskunTuoteTypeahead implements LaskunTuoteTypeaheadBase {
  constructor() {}

  public getTuote(avain: string): Promise<AsiakkaanTuote> {
    return Promise.resolve(null)
  }

  public haeJaSuodata(value: string, laskunValuutta?: string): Promise<TuoteTypeaheadResult> {
    const res: TuoteTypeaheadResult = {
      loytynyt: null,
      suodatetut: [],
    }
    return Promise.resolve(res)
  }
}

type KasiteltyTypeaheadToimittaja = TypeaheadToimittaja & {
  lowerNimi: string
  lowerYtunnus: string
  numeroString: string
}
@Injectable()
export class LaskunToimittajaTypeahead implements LaskunToimittajaTypeaheadBase, OnDestroy {
  private unsubscribe$ = new Subject<void>()
  private typeaheadToimittajatSubject = new BehaviorSubject<KasiteltyTypeaheadToimittaja[]>([])

  constructor(
    private errorHandler: ErrorHandler,
    private laskuIndeksoija: FirestoreIndeksoija,
    private asiakasService: AsiakasService,
    private laskuUriService: LaskuUriService,
    private firebaseLemonaid: FirebaseLemonaid,
  ) {
    this.asiakasService.nykyinenAsiakasAvainObservable
      .pipe(
        switchMap(asiakas => {
          if (asiakas) {
            const typeaheadUri = this.laskuUriService.getTypeaheadToimittajaDokumentinUriNew(asiakas.avain)
            return this.firebaseLemonaid
              .firestoreDoc<TypeaheadToimittajaDokumentti>(typeaheadUri)
              .listen()
              .pipe(
                map(dokumentti => {
                  const toimittajat = dokumentti || {}
                  return Object.keys(toimittajat).map((key, index) => {
                    const value = toimittajat[key]
                    const toimittaja: TypeaheadToimittaja = {
                      avain: key,
                      numero: value.numero,
                      nimi: value.nimi,
                      ytunnus: value.ytunnus,
                      poistettu: value.poistettu,
                    }
                    return toimittaja
                  })
                }),
              )
          }
          return of<TypeaheadToimittaja[]>([])
        }),
        map(toimittajat => {
          if (toimittajat) {
            return toimittajat.map<KasiteltyTypeaheadToimittaja>(toimittaja => {
              const kasitelty = toimittaja as KasiteltyTypeaheadToimittaja
              kasitelty.lowerNimi = this.laskuIndeksoija.poistaValimerkitJaValilyonnit(toimittaja.nimi)
              kasitelty.lowerYtunnus = this.laskuIndeksoija.poistaValimerkitJaValilyonnit(toimittaja.ytunnus)
              kasitelty.numeroString = toimittaja.numero + ''
              return kasitelty
            })
          }
          return []
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        toimittajat => {
          this.typeaheadToimittajatSubject.next(toimittajat || [])
        },
        err => {
          this.errorHandler.handleError(err)
        },
      )
  }

  ngOnDestroy(): void {
    this.typeaheadToimittajatSubject.complete()
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
  }

  public async annaToimittaja(avain: string): Promise<LaskuToimittaja> {
    return null
  }

  public haeJaSuodata(value: string, palautaPoistetut: boolean = false): Promise<ToimittajaTypeaheadResult> {
    const sanat = this.laskuIndeksoija.poistaValimerkitJaNormalisoiValilyonnit(value).split(' ')
    const val = this.laskuIndeksoija.poistaValimerkitJaValilyonnit(value)
    return firstValueFrom(this.typeaheadToimittajatSubject).then(toimittajat => {
      const suodatetut: { toimittaja: TypeaheadToimittaja; pisteet: number }[] = []

      if (!val) {
        // Palautetaan kaikki numerojärjestyksessä
        const kaikkiResult: ToimittajaTypeaheadResult = {
          suodatetut: toimittajat
            .filter(t => {
              if (!palautaPoistetut && t.poistettu) {
                return false
              }
              return true
            })
            .sort((a, b) => {
              return a.numero - b.numero
            }),
        }
        return kaikkiResult
      }

      if (val && val !== '') {
        for (const toimittaja of toimittajat) {
          if (!toimittaja) {
            continue
          }
          if (!palautaPoistetut && toimittaja.poistettu) {
            continue
          }

          let pisteet = 0

          const lowercaseNimi = toimittaja.lowerNimi
          const lowercaseYtunnus = toimittaja.lowerYtunnus
          const numeroString = toimittaja.numeroString

          for (const sana of sanat) {
            if (lowercaseNimi.startsWith(sana)) {
              pisteet += 2
            } else if (lowercaseNimi.indexOf(sana) > -1) {
              pisteet++
            }
            if (lowercaseYtunnus.startsWith(sana)) {
              pisteet += 2
            } else if (lowercaseYtunnus.indexOf(sana) > -1) {
              pisteet++
            }
          }

          if (lowercaseNimi.startsWith(val)) {
            pisteet += 4
          } else if (lowercaseYtunnus.indexOf(val) > -1) {
            pisteet += 2
          }

          if (lowercaseNimi.startsWith(val)) {
            pisteet += 4
          } else if (lowercaseNimi.indexOf(val) > -1) {
            pisteet += 2
          }

          if (lowercaseNimi === val || lowercaseYtunnus === val || numeroString === val) {
            pisteet += 100
          }

          if (pisteet > 0) {
            suodatetut.push({ toimittaja: toimittaja, pisteet: pisteet })
          }
        }
      }

      const loytyneet = suodatetut.sort((a, b) => {
        return b.pisteet - a.pisteet
      })

      const result: ToimittajaTypeaheadResult = {
        suodatetut: (loytyneet.length < 51 ? loytyneet : loytyneet.slice(0, 50)).map(a => a.toimittaja),
      }
      return result
    })
  }
}
